import { getRequest } from '../../utils/apiUtils';

/**
 * Dashboard Service - Centralized API calls for dashboard
 * Following existing architecture patterns
 */
var DashboardService = {
  
  /**
   * Get Today & Next 7 Days data
   * @param {Object} filters - { org_id, facility_ids, year }
   * @returns {Promise} API response
   */
  getTodayNext7Days: function(filters) {
    if (!filters) filters = {};
    
    return new Promise(function(resolve) {
      try {
        var queryParams = DashboardService.buildQueryParams(filters);
        var url = 'api/dashboard/today_next7days' + (queryParams ? '?' + queryParams : '');
        var startTime = performance.now();
        
        getRequest({ url: url })
          .then(function(response) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleResponse(response));
          })
          .catch(function(error) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleError(error, 'Failed to fetch today/next 7 days data'));
          });
      } catch (error) {
        console.error('[API Exception] Today & Next 7 Days -', error);
        resolve(DashboardService.handleError(error, 'Failed to fetch today/next 7 days data'));
      }
    });
  },

  /**
   * Get Completion Trend data
   * @param {Object} filters - { org_id, facility_ids, year }
   * @returns {Promise} API response
   */
  getCompletionTrend: function(filters) {
    if (!filters) filters = {};
    
    return new Promise(function(resolve) {
      try {
        var queryParams = DashboardService.buildQueryParams(filters);
        var url = 'api/dashboard/completion_trend' + (queryParams ? '?' + queryParams : '');
        var startTime = performance.now();
        
        getRequest({ url: url })
          .then(function(response) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleResponse(response));
          })
          .catch(function(error) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleError(error, 'Failed to fetch completion trend data'));
          });
      } catch (error) {
        resolve(DashboardService.handleError(error, 'Failed to fetch completion trend data'));
      }
    });
  },

  /**
   * Get Status Mix data
   * @param {Object} filters - { org_id, facility_ids, year }
   * @returns {Promise} API response
   */
  getStatusMix: function(filters) {
    if (!filters) filters = {};
    
    return new Promise(function(resolve) {
      try {
        var queryParams = DashboardService.buildQueryParams(filters);
        var url = 'api/dashboard/status_mix' + (queryParams ? '?' + queryParams : '');
        var startTime = performance.now();
        
        getRequest({ url: url })
          .then(function(response) {
            var duration = (performance.now() - startTime).toFixed(2);
            var result = (response.data && response.data.result) || {};
            resolve(DashboardService.handleResponse(response));
          })
          .catch(function(error) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleError(error, 'Failed to fetch status mix data'));
          });
      } catch (error) {
        resolve(DashboardService.handleError(error, 'Failed to fetch status mix data'));
      }
    });
  },

  /**
   * Get Frequency Distribution data
   * @param {Object} filters - { org_id, facility_ids, year }
   * @returns {Promise} API response
   */
  getFrequencyDistribution: function(filters) {
    if (!filters) filters = {};
    
    return new Promise(function(resolve) {
      try {
        var queryParams = DashboardService.buildQueryParams(filters);
        var url = 'api/dashboard/frequency_distribution' + (queryParams ? '?' + queryParams : '');
        var startTime = performance.now();
        
        getRequest({ url: url })
          .then(function(response) {
            var duration = (performance.now() - startTime).toFixed(2);
            var result = (response.data && response.data.result) || {};
            resolve(DashboardService.handleResponse(response));
          })
          .catch(function(error) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleError(error, 'Failed to fetch frequency distribution data'));
          });
      } catch (error) {
        resolve(DashboardService.handleError(error, 'Failed to fetch frequency distribution data'));
      }
    });
  },

  /**
   * Get Top Overdue Facilities data
   * @param {Object} filters - { org_id, facility_ids, year }
   * @returns {Promise} API response
   */
  getTopOverdueFacilities: function(filters) {
    if (!filters) filters = {};
    
    return new Promise(function(resolve) {
      try {
        var queryParams = DashboardService.buildQueryParams(filters);
        var url = 'api/dashboard/top_overdue_facilities' + (queryParams ? '?' + queryParams : '');
        var startTime = performance.now();
        
        getRequest({ url: url })
          .then(function(response) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleResponse(response));
          })
          .catch(function(error) {
            var duration = (performance.now() - startTime).toFixed(2);
            resolve(DashboardService.handleError(error, 'Failed to fetch top overdue facilities data'));
          });
      } catch (error) {
        resolve(DashboardService.handleError(error, 'Failed to fetch top overdue facilities data'));
      }
    });
  },

  /**
   * Load all dashboard data
   * @param {Object} filters - Combined filters for all APIs
   * @returns {Promise} Object with all dashboard data
   */
  loadAllDashboardData: function(filters) {
    if (!filters) filters = {};
    
    var loadStartTime = performance.now();
    
    return new Promise(function(resolve, reject) {
      try {
        Promise.all([
          DashboardService.getTodayNext7Days(filters),
          DashboardService.getCompletionTrend(filters),
          DashboardService.getStatusMix(filters),
          DashboardService.getFrequencyDistribution(filters),
          DashboardService.getTopOverdueFacilities(filters)
        ])
        .then(function(results) {
          var totalDuration = (performance.now() - loadStartTime).toFixed(2);
          resolve({
            todayNext7Days: results[0],
            completionTrend: results[1],
            statusMix: results[2],
            frequencyDistribution: results[3],
            topOverdueFacilities: results[4]
          });
        })
        .catch(function(error) {
          reject(error);
        });
      } catch (error) {
        reject(error);
      }
    });
  },

  /**
   * Build query parameters string
   * @param {Object} filters 
   * @returns {string} Query parameters string
   */
  buildQueryParams: function(filters) {
    var params = new URLSearchParams();
    
    // Always include org_id - 0 means "All Group", specific ID means filtered
    if (filters.org_id !== null && filters.org_id !== undefined) {
      params.append('org_id', filters.org_id);
    } else {
      // Default to 0 if not specified (All Group)
      params.append('org_id', '0');
    }
    
    if (filters.facility_ids) {
      // Ensure facility_ids is comma-separated string
      var facilityIds = Array.isArray(filters.facility_ids) 
        ? filters.facility_ids.join(',') 
        : filters.facility_ids;
      params.append('facility_ids', facilityIds);
    }
    
    if (filters.year) {
      params.append('year', filters.year);
    }
    
    var queryString = params.toString();
    
    return queryString;
  },

  /**
   * Handle API response following the contract
   * Backend returns: { success: true, message: "...", result: {...} }
   * @param {Object} response - Axios response
   * @returns {Object} Processed response
   */
  handleResponse: function(response) {
    if (!response || !response.data) {
      return {
        success: false,
        message: 'No response received',
        result: null,
        error: 'No response received'
      };
    }

    var data = response.data;
    
    // Check API contract: success = true (backend uses boolean not string status)
    if (data.success === true) {
      return {
        success: true,
        message: data.message || 'Success',
        result: data.result || {},
        error: null
      };
    } else {
      return {
        success: false,
        message: data.message || 'API returned error',
        result: null,
        error: data.message || 'Unknown error'
      };
    }
  },

  /**
   * Handle API errors
   * @param {Error} error 
   * @param {string} defaultMessage 
   * @returns {Object} Error response
   */
  handleError: function(error, defaultMessage) {
    var errorMsg = error.message || defaultMessage;
    console.error('[Dashboard Service] handleError:', errorMsg);
    console.error('[Dashboard Service] Error object:', error);
    
    return {
      success: false,
      message: errorMsg,
      result: null,
      error: errorMsg
    };
  }
};

export default DashboardService;


// WEBPACK FOOTER //
// ./src/shared/services/dashboard.service.js